import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TeamUserRolesEnum } from "@/enums/team-user-role";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { TeamLicenseInventoryDTO, TeamUserRole } from "@shaarp/common";
import axios from "axios";
import { Loader2Icon } from "lucide-react";
import { FormEvent, useState } from "react";
import { NavLink } from "react-router-dom";
import SelectPrice from "../pricing/select-price";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Separator } from "../ui/separator";
import { useToast, useToastError } from "../ui/use-toast";

export default function DialogSendInvitation({
  onClose,
  inventory,
  defaultPriceId = "",
}: {
  onClose: () => void;
  inventory?: TeamLicenseInventoryDTO;
  /** Pre-selects the license in the price picker (e.g. clicking an empty seat). */
  defaultPriceId?: string;
}) {
  const api = useApi();
  const { toast } = useToast();
  const toastError = useToastError();
  const { t } = useTranslation();

  const [email, setEmail] = useState<string>("");
  const [role, setRole] = useState<TeamUserRole>(TeamUserRolesEnum.MEMBER);
  const [priceId, setPriceId] = useState<string>(defaultPriceId);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!email || !role || (role !== TeamUserRolesEnum.BILLING && !priceId)) {
      toastError();
      return;
    }

    try {
      setIsLoading(true);
      await api.post(`/my/team/invitation`, {
        email,
        role,
        priceId,
      });
      toast({
        variant: "default",
        title: t("team.components.sendInvitation.toast.success.title"),
        description: t(
          "team.components.sendInvitation.toast.success.description",
          { email }
        ),
      });
      onClose();
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        const errorCode = error.response?.data?.code;
        if (errorCode === "INSUFFICIENT_STOCK") {
          toast({
            variant: "destructive",
            title: t("team.sendInvitation.toast.insufficientStock.title"),
            description: (
              <span>
                {t("team.sendInvitation.toast.insufficientStock.description")}{" "}
                <NavLink
                  to="/team/users"
                  className="underline font-medium"
                  onClick={onClose}
                >
                  {t("team.billing.manageUsers")}
                </NavLink>
              </span>
            ),
          });
          return;
        }
      }
      toastError();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-[400px] w-full">
        <DialogHeader>
          <DialogTitle>{t("team.components.sendInvitation.title")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit}>
          <Separator className="my-4" />
          <div className="overflow-auto max-h-[70vh] px-3">
            <div className="space-y-5">
              <div className="grid w-full items-center gap-1.5">
                <Label htmlFor="title">
                  {t("team.components.sendInvitation.form.email.label")}
                </Label>
                <Input
                  type="email"
                  id="email"
                  placeholder={t(
                    "team.components.sendInvitation.form.email.placeholder"
                  )}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="grid w-full items-center gap-1.5">
                <Label htmlFor="ownerType">
                  {t("team.components.sendInvitation.form.role.label")}
                </Label>
                <Select
                  value={role}
                  onValueChange={(value) => setRole(value as TeamUserRole)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value={TeamUserRolesEnum.MANAGER}>
                        {t("team.components.sendInvitation.form.role.manager")}
                      </SelectItem>
                      <SelectItem value={TeamUserRolesEnum.MEMBER}>
                        {t("team.components.sendInvitation.form.role.member")}
                      </SelectItem>
                      <SelectItem value={TeamUserRolesEnum.BILLING}>
                        {t("team.components.sendInvitation.form.role.billing")}
                      </SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
              {role !== TeamUserRolesEnum.BILLING && (
                <div className="grid w-full items-center gap-1.5">
                  <Label htmlFor="licence">
                    {t("team.components.sendInvitation.form.license.label")}
                  </Label>
                  <SelectPrice
                    value={priceId}
                    onChange={setPriceId}
                    inventory={inventory}
                    required
                  />
                </div>
              )}
            </div>
          </div>
          <Separator className="my-4" />
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost">
                {t("team.components.sendInvitation.buttons.close")}
              </Button>
            </DialogClose>
            {isLoading ? (
              <Button disabled>
                <Loader2Icon className="w-4 h-4 animate-spin" />
              </Button>
            ) : (
              <Button type="submit">
                {t("team.components.sendInvitation.buttons.send")}
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
